/**
 * persistence/firestore/ablyVideoSyncConfig.js
 *
 * Remote feature-flag for Ably video-sync, read from Firestore instead of the
 * per-match `use_ably_video_sync` field.
 *
 * Firestore doc:  appConfig/ablyVideoSync
 *   {
 *     mode: "all" | "matches",   // exactly one is active
 *     matchIds: ["123", "456"]   // used only when mode === "matches"
 *   }
 *
 * Anything else (missing doc / unknown mode) ⇒ disabled.
 *
 * A short in-memory TTL cache avoids a Firestore read on every match open.
 */

import { getFirestoreClient } from "./firestoreClient";

const COLLECTION = "appConfig";
const DOC_ID = "ablyVideoSync";
const CACHE_TTL_MS = 60_000;

let _cache = null;
let _cacheAt = 0;

/**
 * @returns {Promise<{ mode: "all"|"matches"|"off", matchIds: string[] }>}
 */
export async function getAblyVideoSyncConfig() {
  if (_cache && Date.now() - _cacheAt < CACHE_TTL_MS) return _cache;

  const fallback = { mode: "off", matchIds: [] };
  try {
    const dbc = await getFirestoreClient();
    if (!dbc) return fallback;

    const { doc, getDoc } = await import("firebase/firestore");
    const snap = await getDoc(doc(dbc, COLLECTION, DOC_ID));
    const data = snap.exists() ? snap.data() : {};

    _cache = {
      mode: data.mode === "all" || data.mode === "matches" ? data.mode : "off",
      matchIds: Array.isArray(data.matchIds) ? data.matchIds.map(String) : [],
    };
    _cacheAt = Date.now();
    return _cache;
  } catch (err) {
    console.warn("[ablyVideoSyncConfig] read failed:", err.message);
    return fallback;
  }
}

/**
 * Resolve whether Ably video-sync is enabled for a given match.
 * @param {string|number} matchId
 * @returns {Promise<boolean>}
 */
export async function isAblyVideoSyncEnabled(matchId) {
  const { mode, matchIds } = await getAblyVideoSyncConfig();
  if (mode === "all") return true;
  if (mode === "matches") return matchIds.includes(String(matchId));
  return false;
}
